// Client-side split-KDF for the optional account password (docs/03 §3a):
//
//   masterKey = Argon2id(password, kdfSalt)              — memory-hard, WASM
//   authKey   = HKDF(masterKey, "merkury-pw-auth-v1")    — sent to server
//   encKey    = HKDF(masterKey, "merkury-pw-enc-v1")     — never leaves client
//   encIdentityKey = XChaCha20-Poly1305(encKey, seed)    — blob server stores
//
// The server only ever handles authKey (which it hashes server-side at rest —
// J4 contract), from which encKey is not derivable — independent HKDF branches.

import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { SIG_DOMAIN } from '@merkury/protocol';
import { concatBytes, randomBytes, utf8 } from '../lib/bytes.js';

// This module is deliberately WASM-free: the Argon2id implementation is INJECTED
// (see `Argon2idProvider`), not imported. The web default lives in the separate
// `argon2Web.ts` (which pulls in hash-wasm) so that importing `kdf.ts` alone —
// as the headless brain does — never drags WASM in; the brain injects the native
// Argon2id seam instead (ios/BOUNDARY.md §2.6). Keep `hash-wasm` OUT of here.

/**
 * Argon2id cost parameters (docs/03 §3a: 64 MB, t=3) for the client-side
 * password → masterKey derivation. Client-only: the server hashes the
 * presented authKey with its own parameters (J4 contract).
 */
export const ARGON2ID_PARAMS = {
  memoryKiB: 64 * 1024,
  iterations: 3,
  parallelism: 1,
  hashLength: 32,
} as const;

export type Argon2idParams = typeof ARGON2ID_PARAMS;

/**
 * One Argon2id derivation request. `password` is the raw bytes to hash — the
 * caller has already NFKC-normalized and UTF-8-encoded any string password.
 * Mirrors the native seam's request shape (runtime/native/kdf.ts) so the
 * native provider satisfies `Argon2idProvider` directly.
 */
export interface Argon2idRequest {
  password: Uint8Array;
  salt: Uint8Array;
  memoryKiB: number;
  iterations: number;
  parallelism: number;
  hashLength: number;
}

/**
 * The injected Argon2id implementation. Web call sites pass `webArgon2id`
 * (identity/argon2Web.ts, hash-wasm); the headless brain passes the native
 * seam (`nativeArgon2id`). Keeping it a parameter is what lets `kdf.ts` stay
 * WASM-free.
 */
export type Argon2idProvider = (req: Argon2idRequest) => Promise<Uint8Array>;

/**
 * Exactly 16 bytes — pinned to the server's decoy-salt length (J4 contract):
 * the challenge endpoint serves a deterministic 16-byte decoy salt for
 * unknown/passwordless accounts, so a different real-salt length would make
 * password-protected accounts distinguishable.
 */
export const KDF_SALT_LENGTH = 16;
export const XCHACHA_NONCE_LENGTH = 24;

export function generateKdfSalt(): Uint8Array {
  return randomBytes(KDF_SALT_LENGTH);
}

export function deriveMasterKey(
  password: string,
  kdfSalt: Uint8Array,
  argon2: Argon2idProvider,
  params: Argon2idParams = ARGON2ID_PARAMS,
): Promise<Uint8Array> {
  // NFKC-normalize then UTF-8-encode here so both the web (hash-wasm) and native
  // providers hash the identical bytes — the account-parity contract.
  return argon2({
    password: utf8(password.normalize('NFKC')),
    salt: kdfSalt,
    memoryKiB: params.memoryKiB,
    iterations: params.iterations,
    parallelism: params.parallelism,
    hashLength: params.hashLength,
  });
}

export function deriveAuthKey(masterKey: Uint8Array): Uint8Array {
  return hkdf(sha256, masterKey, undefined, utf8(SIG_DOMAIN.PW_AUTH), 32);
}

export function deriveEncKey(masterKey: Uint8Array): Uint8Array {
  return hkdf(sha256, masterKey, undefined, utf8(SIG_DOMAIN.PW_ENC), 32);
}

/**
 * Passkey identity-restore (docs/03 §2): turn a WebAuthn PRF output — a
 * per-credential secret the server NEVER sees — into the key that wraps the
 * identity seed. Same HKDF-SHA256 construction and 32-byte output as the
 * password branches, domain-separated by `SIG_DOMAIN.PASSKEY_PRF`; the wrapped
 * blob uses the identical XChaCha20-Poly1305 format as the password path
 * (`encryptIdentitySeed`/`decryptIdentitySeed`), so only the key SOURCE differs.
 */
export function derivePasskeyWrapKey(prfOutput: Uint8Array): Uint8Array {
  return hkdf(sha256, prfOutput, undefined, utf8(SIG_DOMAIN.PASSKEY_PRF), 32);
}

/**
 * The fixed app-wide PRF evaluation salt for identity restore. A PRF salt was
 * never secret NOR needed to be unique (the per-credential PRF secret is what
 * wraps the seed) — but the original per-user random salt forced the client to
 * ask the server for it BEFORE the assertion, which is why restore demanded a
 * typed account UUID. A fixed salt makes the DISCOVERABLE (usernameless)
 * ceremony possible: one tap, no account id. Domain-separated and versioned;
 * legacy blobs wrapped under a per-user salt are still restorable — the
 * assertion evaluates both salts (prf.eval.first/.second) in one ceremony.
 */
export const PRF_RESTORE_SALT: Uint8Array = sha256(utf8('merkury-prf-restore-salt-v1'));

export interface SplitKeys {
  masterKey: Uint8Array;
  /** Sent to the server on password login; hashed server-side at rest. */
  authKey: Uint8Array;
  /** Decrypts `encIdentityKey`. NEVER leaves the client. */
  encKey: Uint8Array;
}

export async function deriveSplitKeys(
  password: string,
  kdfSalt: Uint8Array,
  argon2: Argon2idProvider,
  params: Argon2idParams = ARGON2ID_PARAMS,
): Promise<SplitKeys> {
  const masterKey = await deriveMasterKey(password, kdfSalt, argon2, params);
  return {
    masterKey,
    authKey: deriveAuthKey(masterKey),
    encKey: deriveEncKey(masterKey),
  };
}

/** Blob layout: 24-byte XChaCha nonce || ciphertext (incl. 16-byte Poly1305 tag). */
export function encryptIdentitySeed(encKey: Uint8Array, seed: Uint8Array): Uint8Array {
  const nonce = randomBytes(XCHACHA_NONCE_LENGTH);
  const ciphertext = xchacha20poly1305(encKey, nonce).encrypt(seed);
  return concatBytes(nonce, ciphertext);
}

/** Throws on tampered blob / wrong key (Poly1305 authentication failure). */
export function decryptIdentitySeed(encKey: Uint8Array, blob: Uint8Array): Uint8Array {
  if (blob.length <= XCHACHA_NONCE_LENGTH) throw new Error('encrypted blob too short');
  const nonce = blob.subarray(0, XCHACHA_NONCE_LENGTH);
  const ciphertext = blob.subarray(XCHACHA_NONCE_LENGTH);
  return xchacha20poly1305(encKey, nonce).decrypt(ciphertext);
}
